import React, { Fragment, useState, useEffect } from 'react';
import { cn } from '../../../../lib/utils';
import { Tabs, TabsList, TabsTrigger } from '../../../../Components/ui/tabs';
import { Card, CardContent, CardHeader } from '../../../../Components/ui/card';
import { Button } from '../../../../Components/ui/button';
import { ChevronDown, SquarePen } from 'lucide-react';
import OperatorPopup, { OperatorOption } from './ManageOperatorPopup';
import EditTemplatePopup from './EditTemplatePopup';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '@/src/State/store';
import { useToast } from '../../../../Components/ui/use-toast';
import { Checkbox } from '../../../../Components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../Components/ui/tooltip"
import { ScrollArea } from '../../../../Components/ui/scroll-area';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../Components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../Components/ui/select';

export interface OperatorRow {
  id: number | string;
  channel_config_id: number | string;
  name: string;
  allocation: number;
}

export interface CountryBlock {
  country: string;
  travellers: number;
  operators: OperatorRow[];
}

export type ChannelAllocations = Record<string, CountryBlock[]>;

interface AllocationCardProps {
  allocations: ChannelAllocations;
  defaultChannel?: string;
  onManage?: (channel: string, country: string) => void;
  selectedOperators: Record<string, Record<string, OperatorOption[]>>;
  setSelectedOperators: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, OperatorOption[]>>>
  >;
  editedAllocations: Record<string, Record<string, Record<string, number>>>;
  setEditedAllocations: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, Record<string, number>>>>
  >;
  activeChannel: string;
  setActiveChannel: React.Dispatch<React.SetStateAction<string>>;
  operatorTemplates: Record<string, Record<string, Record<string, any>>>;
  campaign: { startDate: string; endDate: string; };
  availableChannels: { channel_id: number; channel_name: string }[];
  countryToIso: Record<string, string>;
  channelNameToId: Record<string, number>;
  setOperatorTemplates: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, Record<string, any>>>>
  >;
  audienceType?: string;
  campaignStatus?: string;
}

// Utility for deep cloning
function deepClone(obj: any) {
  return JSON.parse(JSON.stringify(obj)); // Simple deep clone method
}

function getCampaignDays(startDate: string, endDate: string): number {
  const [sd, sm, sy] = startDate.split('/').map(Number);
  const [ed, em, ey] = endDate.split('/').map(Number);
  const d1 = new Date(sy, sm - 1, sd);
  const d2 = new Date(ey, em - 1, ed);
  return Math.floor((d2.getTime() - d1.getTime()) / (24 * 60 * 60 * 1000)) + 1;
}

const metaStatusCache = new Map<string, string>();

// ---- Replace your fetchMetaPhoneStatus with this ----
async function fetchMetaPhoneStatus(
  phone_id: string,
  access_token: string
): Promise<string | undefined> {
  try {
    const cacheKey = `${phone_id}|${access_token.slice(0, 12)}`;
    if (metaStatusCache.has(cacheKey)) {
      // console.log("[FB][cache] status:", metaStatusCache.get(cacheKey));
      return metaStatusCache.get(cacheKey);
    }

    const url = `https://graph.facebook.com/v23.0/${encodeURIComponent(
      phone_id
    )}?fields=status&access_token=${encodeURIComponent(access_token)}`;

    // Optional logs to inspect both the preflight (OPTIONS) and real GET
    console.log('[FB] GET', url);

    const resp = await axios.get(url, {
      // If Facebook ever returns non-2xx, we still want to see it
      validateStatus: () => true,
    });

    console.log('[FB] http', resp.status, resp.data);

    const status = (resp?.data as any)?.status as string | undefined;
    if (status) metaStatusCache.set(cacheKey, status);
    return status;
  } catch (e) {
    console.error('[FB] error:', e);
    return undefined;
  }
}

type OptionWithMeta = OperatorOption & {
  api_type?: string;
  channel_id?: number;
  access_token?: string;
  phone_id?: string;
  meta_phone_status?: string;
  warning_reason?: string;
};

async function annotateMetaStatuses(
  options: OperatorOption[]
): Promise<OperatorOption[]> {
  const annotated = await Promise.all(
    (options as OptionWithMeta[]).map(async (op) => {
      // Only check Meta WhatsApp (channel_id === 1 && api_type === 'meta') with both tokens present
      if (
        op.channel_id === 1 &&
        op.api_type?.toLowerCase() === 'meta' &&
        op.phone_id &&
        op.access_token
      ) {
        const status = await fetchMetaPhoneStatus(op.phone_id, op.access_token);
        if (status && status.toLowerCase() === 'pending') {
          return {
            ...op,
            meta_phone_status: status,
            warning_reason: 'phoneid status is pending.',
            disabled: true,
          };
        }
        return { ...op, meta_phone_status: status };
      }
      return op;
    })
  );
  return annotated;
}

const AllocationCard: React.FC<AllocationCardProps> = ({
  allocations,
  defaultChannel,
  onManage,
  selectedOperators,
  setSelectedOperators,
  activeChannel,
  setActiveChannel,
  setEditedAllocations,
  editedAllocations,
  operatorTemplates,
  campaign,
  availableChannels,
  countryToIso,
  channelNameToId,
  setOperatorTemplates,
  audienceType,
  campaignStatus
}) => {
  const data: ChannelAllocations = allocations;
  const channels = availableChannels.map((c) => c.channel_name);
  const countries = data[activeChannel] ?? [];
  const totalTravellers = countries.reduce(
    (sum, country) => sum + country.travellers,
    0
  );
  const adminUrl = useSelector(
    (state: RootState) => state.authentication.adminUrl
  );
  const { toast } = useToast();

  const [openCountries, setOpenCountries] = useState<string[]>([]);
  const [revealed, setRevealed] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [popup, setPopup] = useState<{
    open: boolean;
    channel: string;
    country: string;
    limitToOneOperator?: boolean;
    options?: OperatorOption[];
  } | null>(null);

  //varshini
  // Add after line 191 (after wmTemplateIds state)
  const [wmTemplateIds, setWmTemplateIds] = useState<Record<string, number | null>>({});
  const [selectedOps, setSelectedOps] = useState<any[]>([]);
  const [welcomeTemplateDialog, setWelcomeTemplateDialog] = useState<{
    open: boolean;
    operatorId: string | number;
    channel: string;
    country: string;
    channelConfigId: string | number;
  } | null>(null);

  // const [availableTemplates, setAvailableTemplates] = useState<{
  //   channel_type?: number;           // ← ADD (1=WhatsApp, 2=SMS)
  //   template_id: number;
  //   template_name: string;
  //   ext_template_id?: string;
  //   part_count?: number;
  //   message?: string;
  //   example?: { body_text: string[][] };
  // }[]>([]);
  const [availableTemplates, setAvailableTemplates] = useState<Record<string, {
    channel_type?: number;
    template_id: number;
    template_name: string;
    ext_template_id?: string;
    part_count?: number;
    message?: string;
    example?: { body_text: string[][] };
  }[]>>({});

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');
  const [wmTemplateChannel, setWmTemplateChannel] = useState<string>('');
  const [templateSearch, setTemplateSearch] = useState<string>('');
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState<boolean>(false);



  // Add this function after line 191 (in the component body)
  // const fetchWelcomeTemplates = async (channelConfigId: string | number) => {
  //   try {
  //     const response = await axios.get(
  //       `${adminUrl}/GetWmTemplateIdByChannelConfigId?channel_config_id=${channelConfigId}`
  //     );

  //     if (response.data.status === "Success" && response.data.data && response.data.data.length > 0) {
  //       // Templates available - checkbox will be enabled
  //       setWmTemplateIds(prev => ({
  //         ...prev,
  //         [operatorId]: response.data.data[0].template_id
  //       }));
  //     } else {
  //       setAvailableTemplates([]);
  //       toast({
  //         title: 'No templates found',
  //         description: 'No welcome templates available for this configuration.',
  //         variant: 'destructive',
  //       });
  //     }
  //   } catch (error) {
  //     console.error('Error fetching welcome templates:', error);
  //     setAvailableTemplates([]);
  //     toast({
  //       title: 'Error',
  //       description: 'Failed to fetch welcome templates.',
  //       variant: 'destructive',
  //     });
  //   }
  // };

  const fetchWmTemplateId = async (channelConfigId: string | number) => {
    try {
      const response = await axios.get(
        `${adminUrl}/GetWmTemplateIdByChannelConfigId?channel_config_id=${channelConfigId}`
      );
      debugger
      // ✅ CONSOLE 1 — Check raw API response
      console.log('[WM][1] API raw response for channelConfigId:', channelConfigId, response.data);
      // if (response.data.status === "Success" && response.data.data && response.data.data.length > 0) {
      //   const templates = response.data.data;
      const rawData = typeof response.data.data === 'string'
        ? JSON.parse(response.data.data)   // ← parse the JSON string into a real array
        : response.data.data;

      if (response.data.status === "Success" && Array.isArray(rawData) && rawData.length > 0) {
        const templates = rawData;          // ← use parsed array
        // Templates available - store the first template_id
        // ✅ CONSOLE 2 — Check what gets stored in availableTemplates
        console.log('[WM][2] availableTemplates being set:', JSON.stringify(response.data.data, null, 2));
        const detectedChannel = templates[0]?.channel_type === 1 ? 'WhatsApp' : 'SMS';
        console.log('[WM][2] detectedChannel:', detectedChannel, '| templates:', JSON.stringify(templates, null, 2));

        setWmTemplateChannel(detectedChannel);   // ← STORE detected channel
        // setAvailableTemplates(templates);
        setAvailableTemplates(prev => ({ ...prev, [channelConfigId]: templates }));
        setWmTemplateIds(prev => ({
          ...prev,
          [channelConfigId]: templates[0].template_id
        }));
      } else {
        console.warn('[WM][2] No templates found for channelConfigId:', channelConfigId);
        // No templates available
        // setAvailableTemplates([]);
        setAvailableTemplates(prev => ({ ...prev, [channelConfigId]: [] }));
        setWmTemplateIds(prev => ({
          ...prev,
          [channelConfigId]: null
        }));
      }
    } catch (error) {
      console.error("Error fetching wm_template_id:", error);
      // setAvailableTemplates([]);
      setAvailableTemplates(prev => ({ ...prev, [channelConfigId]: [] }));
      setWmTemplateIds(prev => ({
        ...prev,
        [channelConfigId]: null
      }));
    }
  };
  //   const fetchWmTemplateId = async (channelConfigId: string | number) => {
  //   try {
  //     const response = await axios.get(
  //       `${adminUrl}/GetWmTemplateIdByChannelConfigId?channel_config_id=${channelConfigId}`
  //     );
  //     console.log('[WM][1] API raw response for channelConfigId:', channelConfigId, response.data);

  //     if (response.data.status === "Success" && response.data.data && response.data.data.length > 0) {
  //       const templates = response.data.data;

  //       // ✅ Detect channel from channel_type integer returned by DB (1=WhatsApp, 2=SMS)
  //       const detectedChannel = templates[0]?.channel_type === 1 ? 'WhatsApp' : 'SMS';
  //       console.log('[WM][2] detectedChannel:', detectedChannel, '| templates:', JSON.stringify(templates, null, 2));

  //       setWmTemplateChannel(detectedChannel);   // ← STORE detected channel
  //       setAvailableTemplates(templates);
  //       setWmTemplateIds(prev => ({
  //         ...prev,
  //         [channelConfigId]: templates[0].template_id
  //       }));
  //     } else {
  //       console.warn('[WM][2] No templates found for channelConfigId:', channelConfigId);
  //       setAvailableTemplates([]);
  //       setWmTemplateChannel('');
  //       setWmTemplateIds(prev => ({ ...prev, [channelConfigId]: null }));
  //     }
  //   } catch (error) {
  //     console.error("Error fetching wm_template_id:", error);
  //     setAvailableTemplates([]);
  //     setWmTemplateChannel('');
  //     setWmTemplateIds(prev => ({ ...prev, [channelConfigId]: null }));
  //   }
  // };


  // Helper: extract wm_variables from a template object based on channel type
  // ✅ No change needed here — function already works correctly for both channels
  // because API returns flat { message, example } for both SMS and WhatsApp
  // function extractWmVariables(templateData: any): Record<string, string> | null {
  //   const inner = templateData?.template_content?.template_content;

  //   // WhatsApp: variables inside components[].type === "BODY"
  //   const components = inner?.components;
  //   if (components && Array.isArray(components)) {
  //     const bodyComp = components.find((c: any) => c.type === "BODY");
  //     if (bodyComp?.text) {
  //       const matches = bodyComp.text.match(/\{\{(\d+)\}\}/g) || [];
  //       const exampleVars = bodyComp?.example?.body_text?.[0] || [];
  //       if (matches.length) {
  //         const variables: Record<string, string> = {};
  //         matches.forEach((match: string, idx: number) => {
  //           const num = match.replace(/\{\{|\}\}/g, '');
  //           variables[num] = exampleVars[idx] || '';
  //         });
  //         return variables;
  //       }
  //     }
  //   }

  //   // SMS: variables inside template_content.template_content.message
  //   const message = inner?.message || '';
  //   const bodyTextArr = inner?.example?.body_text?.[0] || [];
  //   if (!message || !bodyTextArr.length) return null;
  //   const matches = message.match(/\{\{(\d+)\}\}/g) || [];
  //   if (!matches.length) return null;
  //   const variables: Record<string, string> = {};
  //   matches.forEach((match: string, idx: number) => {
  //     const num = match.replace(/\{\{|\}\}/g, '');
  //     variables[num] = bodyTextArr[idx] || '';
  //   });
  //   return variables;
  // }


  function extractWmVariables(templateData: any): Record<string, string> | null {
    // ✅ Correct path for BOTH channels: template_content.template
    const inner = templateData?.template_content?.template;

    if (!inner) return null;

    // ── WhatsApp: variables in components[type=BODY].text ──
    const components = inner?.components;
    if (components && Array.isArray(components)) {
      const bodyComp = components.find((c: any) => c.type === "BODY");
      if (bodyComp?.text) {
        const matches = bodyComp.text.match(/\{\{(\d+)\}\}/g) || [];
        const exampleVars = bodyComp?.example?.body_text?.[0] || [];
        if (matches.length) {
          const variables: Record<string, string> = {};
          matches.forEach((match: string, idx: number) => {
            const num = match.replace(/\{\{|\}\}/g, '');
            variables[num] = exampleVars[idx] || '';
          });
          return variables;
        }
      }
      // WhatsApp template has no variables in BODY → return null
      return null;
    }

    // ── SMS: variables in template.message + template.example ──
    const message = inner?.message || '';
    const bodyTextArr = inner?.example?.body_text?.[0] || [];
    if (!message || !bodyTextArr.length) return null;
    const matches = message.match(/\{\{(\d+)\}\}/g) || [];
    if (!matches.length) return null;
    const variables: Record<string, string> = {};
    matches.forEach((match: string, idx: number) => {
      const num = match.replace(/\{\{|\}\}/g, '');
      variables[num] = bodyTextArr[idx] || '';
    });
    return variables;
  }

  function getWmTemplateId(templateData: any, channelType: number | undefined): number | string | null {
    if (channelType === 1) {
      // WhatsApp: use ext_template_id
      const extId = templateData?.ext_template_id;
      return (extId && extId !== '') ? extId : null;
    } else {
      // SMS: use internal template_id
      return templateData?.template_id || null;
    }
  }
  // const fetchWmTemplateId = async (channelConfigId: string | number) => {
  //   try {
  //     const response = await axios.get(
  //       `${adminUrl}/GetWmTemplateIdByChannelConfigId?channel_config_id=${channelConfigId}`
  //     );
  //     if (response.data.status === "Success" && response.data.data?.length > 0) {
  //       // ✅ Store ALL templates for this channelConfigId
  //       setAvailableTemplates(response.data.data);
  //     } else {
  //       setAvailableTemplates([]);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching wm_template_id:", error);
  //     setAvailableTemplates([]);
  //   }
  // };
  // Auto-reveal countries that have selected operators
  useEffect(() => {
    const newRevealed: Record<string, Record<string, boolean>> = {};

    Object.keys(selectedOperators).forEach((channel) => {
      if (!newRevealed[channel]) newRevealed[channel] = {};

      Object.keys(selectedOperators[channel] || {}).forEach((country) => {
        const operators = selectedOperators[channel][country] || [];
        if (operators.length > 0) {
          newRevealed[channel][country] = true;
        }
      });
    });

    setRevealed(newRevealed);

    // Also auto-open countries that have operators
    const autoOpenCountries: string[] = [];
    const currentChannelOperators = selectedOperators[activeChannel] || {};
    Object.keys(currentChannelOperators).forEach((country) => {
      if (currentChannelOperators[country]?.length > 0) {
        autoOpenCountries.push(country);
      }
    });

    setOpenCountries((prev) => {
      const combined = Array.from(new Set([...prev, ...autoOpenCountries]));
      return combined;
    });
  }, [selectedOperators, activeChannel]);

  const isRevealed = (ch: string, ct: string) => {
    const hasOperators = selectedOperators[ch]?.[ct]?.length > 0;
    return revealed[ch]?.[ct] || hasOperators;
  };

  const revealCountry = (ch: string, ct: string) =>
    setRevealed((prev) => ({
      ...prev,
      [ch]: { ...prev[ch], [ct]: true },
    }));

  // Track which operator config is being edited with its index (configIdx)
  const [editTemplateDialog, setEditTemplateDialog] = useState<{
    open: boolean;
    templateId: string | number;
    channel: string;
    country: string;
    initialContent: any;
    configIdx: number;
  } | null>(null);


  const campaignDays = getCampaignDays(campaign.startDate, campaign.endDate);

  useEffect(() => {
    // Fetch wm_template_ids for all selected operators
    Object.keys(selectedOperators).forEach(channel => {
      Object.keys(selectedOperators[channel] || {}).forEach(country => {
        const ops = selectedOperators[channel][country] || [];
        ops.forEach(op => {
          if (op.channel_config_id && !wmTemplateIds[op.id]) {
            fetchWmTemplateId(op.channel_config_id);
          }
        });
      });
    });
  }, [selectedOperators]);

  // SMS short code expansion in popup
  const handlePopupClose = (selectedOps: OperatorOption[]) => {
    if (popup) {
      setSelectedOperators((prev) => ({
        ...prev,
        [popup.channel]: {
          ...prev[popup.channel],
          [popup.country]: selectedOps,
        },
      }));

      // Fair distribution logic
      const total = 100;
      const baseAllocation =
        selectedOps.length > 0 ? Math.floor(total / selectedOps.length) : 0;
      const remaining =
        selectedOps.length > 0
          ? total - baseAllocation * selectedOps.length
          : 0;

      const newAllocations: Record<string, number> = {};
      selectedOps.forEach((op, index) => {
        newAllocations[op.id.toString()] =
          baseAllocation + (index === 0 ? remaining : 0);
      });

      setEditedAllocations((prev) => ({
        ...prev,
        [popup.channel]: {
          ...prev[popup.channel],
          [popup.country]: newAllocations,
        },
      }));

      revealCountry(popup.channel, popup.country);
      onManage?.(popup.channel, popup.country);
    }
    setPopup(null);
  };

  const getOperatorAllocations = (channel: string, country: string) => {
    const selected = selectedOperators[channel]?.[country] || [];
    const defaultAllocation =
      selected.length > 0 ? Math.floor(100 / selected.length) : 0;
    return selected.map((op) => ({
      ...op,
      allocation:
        editedAllocations[channel]?.[country]?.[op.id.toString()] ??
        defaultAllocation,
    }));
  };

  const handleAllocationChange = (
    channel: string,
    country: string,
    opId: string | number,
    value: string,
    operatorsCount: number
  ) => {
    const numValue = value === '' ? 0 : parseInt(value) || 0;

    setEditedAllocations((prev) => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [country]: {
          ...prev[channel]?.[country],
          [opId.toString()]: numValue,
        },
      },
    }));
  };





  const getCurrentSelection = (channel: string, country: string) =>
    selectedOperators[channel]?.[country] || [];

  const validateTotal = (total: number) => total !== 100;

  if (!channels.length) return null;

  console.log("campaign status for test", campaignStatus);




  return (
    <>
      <Card className='border rounded-lg'>
        <CardHeader className='p-0 border-b'>
          <Tabs value={activeChannel} onValueChange={setActiveChannel}>
            <TabsList className='inline-flex p-1 m-3 rounded-lg bg-muted'>
              {channels.map((ch, i) => (
                <React.Fragment key={ch}>
                  <TabsTrigger
                    value={ch}
                    className={cn(
                      'rounded px-3 py-1 text-sm font-medium transition-colors',
                      'data-[state=active]:bg-[#3A85F7] data-[state=active]:text-[#F8FAFC]',
                      'data-[state=inactive]:text-[#64748B]'
                    )}
                  >
                    {ch}
                  </TabsTrigger>
                  {i !== channels.length - 1 && (
                    <span className='self-stretch w-px mx-1 bg-black' />
                  )}
                </React.Fragment>
              ))}
            </TabsList>
          </Tabs>
        </CardHeader>

        <CardContent className='p-0'>
          {countries.map((ct, idx) => {
            const isOpen = openCountries.includes(ct.country);
            const visible = isRevealed(activeChannel, ct.country);
            const selectedOps = getOperatorAllocations(
              activeChannel,
              ct.country
            );
            const disableManageOperatorsBtn =
              audienceType === 'Reuse Audience' ||  // ← ADD THIS
              (selectedOps || []).some(
                (op) =>
                  typeof op.status !== 'undefined' &&
                  op.status !== null &&
                  String(op.status).trim() !== ''
              );
            const total = selectedOps.reduce((s, o) => s + o.allocation, 0);
            const limitToOneOperator = ct.travellers <= campaignDays;

            return (
              <Fragment key={ct.country}>
                <button
                  type='button'
                  className='flex min-w-[214px] h-12 items-center justify-start px-4 py-3 text-sm font-semibold text-[#020617]'
                  onClick={() =>
                    setOpenCountries((prev) =>
                      isOpen
                        ? prev.filter((c) => c !== ct.country)
                        : [...prev, ct.country]
                    )
                  }
                >
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform text-muted-foreground mt-1',
                      isOpen && 'rotate-180'
                    )}
                  />
                  <span className='ml-1'>
                    {ct.country} &nbsp;({ct.travellers})
                  </span>
                </button>

                {isOpen && (
                  <div className='px-4 pb-6 space-y-4'>
                    <Button
                      variant='ghost'
                      size='lg'
                      className={
                        'h-[40px] w-full justify-start gap-1 border text-[#020617] bg-muted/70 border-muted-foreground py-2 px-3 -mt-0 ' +
                        (disableManageOperatorsBtn
                          ? 'opacity-60 cursor-not-allowed pointer-events-none'
                          : 'hover:bg-muted')
                      }
                      disabled={disableManageOperatorsBtn}
                      tabIndex={disableManageOperatorsBtn ? -1 : 0}
                      onClick={
                        disableManageOperatorsBtn
                          ? undefined
                          : async () => {
                            const iso = countryToIso[ct.country];
                            const channelId = channelNameToId[activeChannel];
                            const isSMS = String(activeChannel)
                              .toLowerCase()
                              .includes('sms');
                            if (!iso || !channelId) return;

                            try {
                              const resp = await axios.post(
                                `${adminUrl}/GetOperatorWithChannelConfigsByIsoAndChannel`,
                                {
                                  iso,
                                  channel_id: channelId,
                                }
                              );
                              if (
                                resp.data.status === 'Success' &&
                                Array.isArray(
                                  resp.data.operatorWithChannelConfigs
                                )
                              ) {
                                let operatorOptions: OperatorOption[] = [];
                                resp.data.operatorWithChannelConfigs.forEach(
                                  (op: any) => {
                                    if (
                                      isSMS &&
                                      op.channel_config &&
                                      typeof op.channel_config === 'string'
                                    ) {
                                      try {
                                        const parsedConfig = JSON.parse(
                                          op.channel_config
                                        );
                                        let shortCodes: number[] | string[] =
                                          [];
                                        if (
                                          parsedConfig.config &&
                                          (parsedConfig.config
                                            .sms_short_codes ||
                                            parsedConfig.config.ShortCodes)
                                        ) {
                                          shortCodes = JSON.parse(
                                            parsedConfig.config
                                              .sms_short_codes || '[]'
                                          );
                                        }
                                        if (
                                          Array.isArray(shortCodes) &&
                                          shortCodes.length > 0
                                        ) {
                                          shortCodes.forEach((sc: any) => {
                                            operatorOptions.push({
                                              id: `${op.channel_config_id}_${sc}`,
                                              channel_config_id:
                                                op.channel_config_id,
                                              label: `${op.channel_config_name} - ${sc}`,
                                              short_code: sc,
                                              partner_id: op.partner_id,
                                              channel_config:
                                                op.channel_config,
                                              channel_config_name:
                                                op.channel_config_name,
                                              auto_approval: op.auto_approval,
                                            });
                                          });
                                        } else {
                                          let phone_id: string = '';
                                          let access_token: string = '';
                                          let api_type: string = '';
                                          let ttl_hours: number = 0;
                                          try {
                                            let parsed =
                                              typeof op.channel_config ===
                                                'string'
                                                ? JSON.parse(
                                                  op.channel_config
                                                )
                                                : op.channel_config;
                                            if (
                                              parsed?.channel_type?.toLowerCase() ===
                                              'whatsapp'
                                            ) {
                                              api_type = (
                                                parsed?.api_type || ''
                                              ).toString();
                                              if (
                                                api_type.toLowerCase() ===
                                                'meta'
                                              ) {
                                                phone_id =
                                                  parsed?.config?.phone_id ||
                                                  parsed?.config?.phoneId ||
                                                  '';
                                                access_token =
                                                  parsed?.config
                                                    ?.access_token ||
                                                  parsed?.config
                                                    ?.accessToken ||
                                                  parsed?.config?.token ||
                                                  '';

                                              }
                                              ttl_hours = parsed?.max_validity_hours ? Number(parsed.max_validity_hours) * 3600 : 0;
                                            }
                                          } catch { }
                                          operatorOptions.push({
                                            id: op.channel_config_id,
                                            channel_config_id:
                                              op.channel_config_id,
                                            label: op.channel_config_name,
                                            partner_id: op.partner_id,
                                            channel_config: op.channel_config,
                                            channel_config_name:
                                              op.channel_config_name,
                                            auto_approval: op.auto_approval,
                                            phone_id,
                                            access_token,
                                            api_type,
                                            channel_id: op.channel_id,
                                            ttl_hours
                                          });
                                        }
                                      } catch (err) {
                                        operatorOptions.push({
                                          id: op.channel_config_id,
                                          channel_config_id:
                                            op.channel_config_id,
                                          label: op.channel_config_name,
                                          partner_id: op.partner_id,
                                          channel_config: op.channel_config,
                                          channel_config_name:
                                            op.channel_config_name,
                                          auto_approval: op.auto_approval,
                                        });
                                      }
                                    } else {
                                      let phone_id: string = '';
                                      let access_token: string = '';
                                      let api_type: string = '';
                                      let ttl_hours: number = 0;
                                      try {
                                        let parsed =
                                          typeof op.channel_config ===
                                            'string'
                                            ? JSON.parse(op.channel_config)
                                            : op.channel_config;
                                        if (
                                          parsed?.channel_type?.toLowerCase() ===
                                          'whatsapp'
                                        ) {
                                          api_type = (
                                            parsed?.api_type || ''
                                          ).toString();
                                          if (
                                            api_type.toLowerCase() === 'meta'
                                          ) {
                                            phone_id =
                                              parsed?.config?.phone_id ||
                                              parsed?.config?.phoneId ||
                                              '';
                                            access_token =
                                              parsed?.config?.access_token ||
                                              parsed?.config?.accessToken ||
                                              parsed?.config?.token ||
                                              '';
                                          }
                                          ttl_hours = parsed?.max_validity_hours ? Number(parsed.max_validity_hours) * 3600 : 0;
                                        }
                                      } catch { }
                                      operatorOptions.push({
                                        id: op.channel_config_id,
                                        channel_config_id:
                                          op.channel_config_id,
                                        label: op.channel_config_name,
                                        partner_id: op.partner_id,
                                        channel_config: op.channel_config,
                                        channel_config_name:
                                          op.channel_config_name,
                                        auto_approval: op.auto_approval,
                                        phone_id,
                                        access_token,
                                        api_type,
                                        channel_id: op.channel_id,
                                        ttl_hours
                                      });
                                    }
                                  }
                                );
                                const optionsWithStatus =
                                  await annotateMetaStatuses(operatorOptions);
                                setPopup({
                                  open: true,
                                  channel: activeChannel,
                                  country: ct.country,
                                  limitToOneOperator,
                                  options: optionsWithStatus,
                                });
                              } else {
                                toast({
                                  title: 'Error',
                                  description:
                                    'No operators found for this country.',
                                  variant: 'default',
                                });
                              }
                            } catch (err) {
                              toast({
                                title: 'Error',
                                description:
                                  'Something went wrong. Please try again.',
                                variant: 'default',
                              });
                            }
                          }
                      }
                    >
                      +<span>Manage Operators</span>
                    </Button>

                    {limitToOneOperator && (
                      <div className='text-xs text-[#64748B] mb-2'>
                        Note: If the number of travellers for a country matches
                        the total number of campaign days, only one operator can
                        be allocated for that country.
                      </div>
                    )}

                    {visible && (
                      <>
                        {audienceType === 'Reuse Audience' && (
                          <div className="flex items-center gap-2 mb-2 px-3 py-1 rounded border border-blue-200 text-xs text-blue-700">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
                            </svg>
                            Welcome message template is not applicable for Reuse Audience campaigns.
                          </div>
                        )}
                        <div
                          className={`grid ${audienceType === "Reuse Audience"
                            ? "grid-cols-[2fr_120px_100px]"
                            : "grid-cols-[2fr_85px_120px_100px]"
                            } items-center gap-3`}
                        >
                          <span>Operator</span>
                          {audienceType !== "Reuse Audience" && <span>WM</span>}
                          <span>Allocations(%)</span>
                          <span className="text-center">Content</span>
                        </div>

                        {selectedOps.map((op, opIdx) => {
                          return (
                            <div
                              key={op.id}
                              className={`grid ${audienceType === "Reuse Audience"
                                ? "grid-cols-[2fr_120px_100px]"
                                : "grid-cols-[2fr_85px_120px_100px]"
                                } items-center gap-3`}
                            >
                              <input
                                readOnly
                                value={op.label}
                                className={`h-8 w-full rounded border border-[#E2E8F0] px-3 text-sm cursor-default ${audienceType === 'Reuse Audience'
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-white text-[#020617]'
                                  }`}
                              />
                              {audienceType !== "Reuse Audience" && (
                                <div className='flex items-center gap-4 whitespace-nowrap'>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="relative ml-3">
                                        <Checkbox
                                          id={`welcome-${op.id}`}
                                          checked={op.welcomeMessage || false}
                                          // CHANGE TO:
                                          disabled={
                                            (typeof op.status !== 'undefined' && op.status !== null && String(op.status).trim() !== '') ||
                                            wmTemplateIds[op.channel_config_id] === null || wmTemplateIds[op.channel_config_id] === undefined
                                          }
                                          onCheckedChange={async (checked) => {
                                            const isChecked = checked === true;

                                            if (isChecked) {
                                              // Update state to check the checkbox
                                              const updatedOps = selectedOps.map(operator =>
                                                operator.id === op.id
                                                  ? { ...operator, welcomeMessage: true }
                                                  : operator
                                              );

                                              setSelectedOperators(prev => ({
                                                ...prev,
                                                [activeChannel]: {
                                                  ...prev[activeChannel],
                                                  [ct.country]: updatedOps
                                                }
                                              }));

                                              // Update local selectedOps
                                              setSelectedOps(updatedOps);

                                              // Fetch templates
                                              await fetchWmTemplateId(op.channel_config_id);

                                              // Pre-populate if already selected
                                              if (op.wm_template_id && op.wm_template_name) {
                                                setSelectedTemplateId(op.wm_template_id);
                                                setSelectedTemplateName(op.wm_template_name);
                                              }
                                              // fetchWmTemplateId(op.channel_config_id);
                                              // Open dialog
                                              setWelcomeTemplateDialog({
                                                open: true,
                                                operatorId: op.id,
                                                channel: activeChannel,
                                                country: ct.country,
                                                channelConfigId: op.channel_config_id,
                                              });
                                            } else {
                                              // Uncheck and remove template
                                              const updatedOps = selectedOps.map(operator =>
                                                operator.id === op.id
                                                  ? { ...operator, welcomeMessage: false, wm_template_id: null, wm_template_name: null }
                                                  : operator
                                              );

                                              setSelectedOperators(prev => ({
                                                ...prev,
                                                [activeChannel]: {
                                                  ...prev[activeChannel],
                                                  [ct.country]: updatedOps
                                                }
                                              }));
                                            }
                                          }}
                                          className="h-4 w-4 data-[state=checked]:bg-[#3A85F7] data-[state=checked]:border-[#3A85F7]"
                                        />
                                      </div>
                                    </TooltipTrigger>
                                    {op.welcomeMessage && op.wm_template_name ? (
                                      <TooltipContent className="bg-black text-white border-black">
                                        <p>{op.wm_template_name}</p>
                                      </TooltipContent>
                                    ) : (wmTemplateIds[op.channel_config_id] === null || wmTemplateIds[op.channel_config_id] === undefined) ? (
                                      <TooltipContent className="bg-black text-white border-black">
                                        <p>Welcome template not available for this config</p>
                                      </TooltipContent>
                                    ) : null}
                                  </Tooltip>
                                </div>
                              )}
                              <div className='relative'>
                                {audienceType === 'Reuse Audience' ? (
                                  // ✅ Reuse Audience: always read-only, show pre-filled allocation
                                  <input
                                    type='text'
                                    readOnly
                                    value={String(op.allocation)}
                                    className='h-8 w-[96px] ml-3 rounded border border-[#E2E8F0] px-3 text-sm text-[#020617] text-left bg-gray-100 cursor-not-allowed text-gray-400'
                                  />
                                ) : typeof op.status === 'undefined' || op.status === null ? (
                                  <input
                                    type='text'
                                    inputMode='numeric'
                                    pattern='[0-9]*'
                                    value={
                                      op.allocation === 0
                                        ? ''
                                        : String(op.allocation)
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /\D/g,
                                        ''
                                      );
                                      if (value === '') {
                                        handleAllocationChange(
                                          activeChannel,
                                          ct.country,
                                          op.id,
                                          '',
                                          selectedOps.length
                                        );
                                        return;
                                      }
                                      const number = Number(value);
                                      if (
                                        selectedOps.length > 1 &&
                                        number >= 100
                                      )
                                        return;
                                      if (
                                        selectedOps.length === 1 &&
                                        number > 100
                                      )
                                        return;
                                      if (
                                        number >= 0 &&
                                        number <=
                                        (selectedOps.length === 1 ? 100 : 99)
                                      ) {
                                        handleAllocationChange(
                                          activeChannel,
                                          ct.country,
                                          op.id,
                                          value,
                                          selectedOps.length
                                        );
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = e.target.value.replace(
                                        /\D/g,
                                        ''
                                      );
                                      if (value === '' || Number(value) === 0) {
                                        if (selectedOps.length > 1) {
                                          handleAllocationChange(
                                            activeChannel,
                                            ct.country,
                                            op.id,
                                            '1',
                                            selectedOps.length
                                          );
                                        } else {
                                          handleAllocationChange(
                                            activeChannel,
                                            ct.country,
                                            op.id,
                                            '100',
                                            selectedOps.length
                                          );
                                        }
                                      }
                                    }}
                                    readOnly={selectedOps.length === 1}
                                    tabIndex={selectedOps.length === 1 ? -1 : 0}
                                    className={`h-8 w-[96px] ml-3 rounded border border-[#E2E8F0] px-3 text-sm text-[#020617] text-left ${selectedOps.length === 1
                                      ? 'bg-gray-100 cursor-not-allowed text-gray-400'
                                      : 'bg-white'
                                      }`}
                                  />
                                ) : (
                                  <input
                                    type='text'
                                    value={
                                      op.travellerCount && ct.travellers
                                        ? Math.round(
                                          (op.travellerCount /
                                            ct.travellers) *
                                          100
                                        )
                                        : 0
                                    }
                                    readOnly
                                    className='h-8 w-[96px] ml-3 rounded border border-[#E2E8F0] px-3 text-sm text-[#020617] text-left bg-gray-100 cursor-not-allowed text-gray-400'
                                  />
                                )}
                                <span className='absolute text-xs -translate-y-1/2 right-3 top-1/2'>
                                  %
                                </span>
                              </div>

                              <div className='flex justify-center'>
                                <span
                                  className={`inline-block ${op.status &&
                                    op.status.toLowerCase() !== 'rejected' &&
                                    !(campaignStatus === 'sandbox' &&
                                      (op.status.toLowerCase() === 'pending' ||
                                        op.status.toLowerCase() === 'rejected'))
                                    ? 'pointer-events-none cursor-not-allowed'
                                    : 'cursor-pointer'
                                    }`}
                                  title={
                                    op.status &&
                                      op.status.toLowerCase() !== 'rejected' &&
                                      !(campaignStatus === 'sandbox' &&
                                        (op.status.toLowerCase() === 'pending' ||
                                          op.status.toLowerCase() === 'rejected'))
                                      ? 'Editing is allowed only for rejected or new configs'
                                      : 'Edit Template'
                                  }
                                  tabIndex={-1}
                                >
                                  <SquarePen

                                    className='h-4 w-4 text-[#020617]'
                                    onClick={
                                      (op.status &&
                                        op.status.toLowerCase() !== 'rejected' &&
                                        !(campaignStatus === 'sandbox' &&
                                          (op.status.toLowerCase() === 'pending' ||
                                            op.status.toLowerCase() === 'rejected')))
                                        ? undefined
                                        : () => {
                                          const templateData =
                                            operatorTemplates?.[activeChannel]?.[ct.country]?.[op.id] || {};
                                          const phone_id = templateData.phone_id || '';
                                          const template_id = templateData.template_id || '';
                                          const channel_config_id =
                                            templateData.channel_config_id || op.channel_config_id || '';

                                          console.log('Opening edit popup with:', {
                                            phone_id,
                                            template_id,
                                            channel_config_id,
                                            initialContent: templateData,
                                          });

                                          setEditTemplateDialog({
                                            open: true,
                                            templateId: op.id,
                                            channel: activeChannel,
                                            country: ct.country,
                                            initialContent: {
                                              ...templateData,
                                              phone_id,
                                              template_id,
                                              channel_config_id,
                                            },
                                            configIdx: opIdx + 1,
                                          });
                                        }
                                    }
                                    style={
                                      (op.status &&
                                        op.status.toLowerCase() !== 'rejected' &&
                                        !(campaignStatus === 'sandbox' &&
                                          (op.status.toLowerCase() === 'pending' ||
                                            op.status.toLowerCase() === 'rejected')))
                                        ? { color: '#B0B0B0' }
                                        : {}
                                    }
                                  />
                                </span>
                              </div>

                              {editTemplateDialog?.open &&
                                editTemplateDialog.templateId === op.id &&
                                editTemplateDialog.channel === activeChannel &&
                                editTemplateDialog.country === ct.country && (
                                  <EditTemplatePopup
                                    open={editTemplateDialog.open}
                                    templateId={editTemplateDialog.templateId}
                                    channel={editTemplateDialog.channel}
                                    country={editTemplateDialog.country}
                                    onClose={() => setEditTemplateDialog(null)}
                                    initialContent={
                                      operatorTemplates?.[
                                      editTemplateDialog.channel
                                      ]?.[editTemplateDialog.country]?.[
                                      editTemplateDialog.templateId
                                      ] || null
                                    }
                                    onSave={(content) => {
                                      setOperatorTemplates((prev) => ({
                                        ...prev,
                                        [editTemplateDialog.channel]: {
                                          ...(prev[
                                            editTemplateDialog.channel
                                          ] || {}),
                                          [editTemplateDialog.country]: {
                                            ...(prev[
                                              editTemplateDialog.channel
                                            ]?.[editTemplateDialog.country] ||
                                              {}),
                                            [editTemplateDialog.templateId]:
                                              content,
                                          },
                                        },
                                      }));
                                      setEditTemplateDialog(null);
                                    }}
                                    allTemplates={
                                      operatorTemplates?.[
                                        editTemplateDialog.channel
                                      ]
                                        ? Object.values(
                                          operatorTemplates[
                                          editTemplateDialog.channel
                                          ]
                                        ).flatMap((countryObj) =>
                                          Object.values(countryObj || {})
                                        )
                                        : []
                                    }
                                    configIdx={editTemplateDialog.configIdx}
                                  />
                                )}
                            </div>
                          );
                        })}


                        <div className='flex items-center gap-3'>
                          <div className='flex-1 h-[10px] rounded bg-gray-200 relative overflow-hidden'>
                            <div
                              className={`h-full rounded transition-all duration-300 ${validateTotal(total)
                                ? 'bg-red-500'
                                : 'bg-[#3A85F7]'
                                }`}
                              style={{ width: `${Math.min(total, 100)}%` }}
                            />
                          </div>
                          <div
                            className={`text-[11px] font-medium ${validateTotal(total)
                              ? 'text-red-500'
                              : 'text-[#020617]'
                              }`}
                          >
                            {total} %
                          </div>
                        </div>
                        {validateTotal(total) && (
                          <div className='mt-1 text-xs text-red-500'>
                            Total allocation must equal 100%
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {idx !== countries.length - 1 && <div className='border-b' />}
              </Fragment>
            );
          })}
        </CardContent>

        <div className='px-6 pt-8 pb-5'>
          <label className='block text-sm font-medium text-[#020617] mb-1'>
            Total Traveller Count - {activeChannel}
          </label>
          <input
            type='text'
            readOnly
            value={totalTravellers}
            className='h-10 w-full rounded border border-[#E2E8F0] bg-muted text-foreground/80 px-3 cursor-default'
          />
        </div>
      </Card>

      {popup && (
        <OperatorPopup
          open={popup.open}
          country={popup.country}
          options={popup.options || []}
          initial={getCurrentSelection(popup.channel, popup.country)}
          onClose={handlePopupClose}
          limitToOneOperator={popup.limitToOneOperator}
        />
      )}

      {/* Welcome Template Selection Dialog */}
      {welcomeTemplateDialog && (
        <Dialog
          open={welcomeTemplateDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              // Cancel: close dialog and uncheck checkbox
              const updatedOps = selectedOps.map(operator =>
                operator.id === welcomeTemplateDialog.operatorId
                  ? { ...operator, welcomeMessage: false, wm_template_id: null }
                  : operator
              );

              setSelectedOperators(prev => ({
                ...prev,
                [welcomeTemplateDialog.channel]: {
                  ...prev[welcomeTemplateDialog.channel],
                  [welcomeTemplateDialog.country]: updatedOps
                }
              }));

              setWelcomeTemplateDialog(null);
              setSelectedTemplateId(null);
              setSelectedTemplateName('');
            }
          }}
        >
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Link Welcome Template</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Template</label>
                <div className="relative w-[500px]">
                  {/* Trigger button */}
                  <button
                    type="button"
                    className="w-full h-10 rounded border border-[#E2E8F0] px-3 text-sm text-left flex items-center justify-between bg-white"
                    onClick={() => {
                      setIsTemplateDropdownOpen(prev => !prev);
                      setTemplateSearch('');
                    }}
                  >
                    <span className={selectedTemplateName ? 'text-[#020617]' : 'text-gray-400'}>
                      {selectedTemplateName || 'Select a template'}
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </button>

                  {/* Dropdown panel */}
                  {isTemplateDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded border border-[#E2E8F0] bg-white shadow-md">
                      {/* Search input */}
                      <div className="p-2 border-b border-[#E2E8F0]">
                        <input
                          autoFocus
                          type="text"
                          placeholder="Search templates..."
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                          className="w-full h-8 rounded border border-[#E2E8F0] px-3 text-sm outline-none focus:border-[#E2E8F0]"
                        />
                      </div>

                      {/* Scrollable list */}
                      <ScrollArea className="h-[200px]">
                        {(availableTemplates[welcomeTemplateDialog.channelConfigId] || [])
                          .filter(t => t.template_name.toLowerCase().includes(templateSearch.toLowerCase()))
                          .length === 0 ? (
                          <div className="px-3 py-4 text-sm text-gray-400 text-center">No templates found</div>
                        ) : (
                          (availableTemplates[welcomeTemplateDialog.channelConfigId] || [])
                            .filter(t => t.template_name.toLowerCase().includes(templateSearch.toLowerCase()))
                            .map((template) => (
                              <div
                                key={template.template_id}
                                onClick={() => {
                                  setSelectedTemplateId(template.template_id);
                                  setSelectedTemplateName(template.template_name);
                                  setTemplateSearch('');
                                  setIsTemplateDropdownOpen(false);
                                }}
                                className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 flex items-center justify-between gap-2 ${selectedTemplateId === template.template_id ? 'bg-gray-100 text-[#020617]' : 'text-[#020617]'}`}
                              >
                                <span className="min-w-0 truncate">{template.template_name}</span>
                                <span className="shrink-0 w-4">
                                  {selectedTemplateId === template.template_id && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#020617]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </span>
                              </div>
                            ))
                        )}
                      </ScrollArea>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                className="px-3 py-1 w-[100px]"
                onClick={() => {
                  // Cancel: close dialog and uncheck checkbox
                  const updatedOps = selectedOps.map(operator =>
                    operator.id === welcomeTemplateDialog.operatorId
                      ? { ...operator, welcomeMessage: false, wm_template_id: null }
                      : operator
                  );

                  setSelectedOperators(prev => ({
                    ...prev,
                    [welcomeTemplateDialog.channel]: {
                      ...prev[welcomeTemplateDialog.channel],
                      [welcomeTemplateDialog.country]: updatedOps
                    }
                  }));
                  // setAvailableTemplates([]); // ← ADD this
                  setAvailableTemplates(prev => ({ ...prev, [welcomeTemplateDialog.channelConfigId]: [] }));
                  setWelcomeTemplateDialog(null);
                  setSelectedTemplateId(null);
                  setSelectedTemplateName('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!selectedTemplateId) {
                    toast({ title: 'Error', description: 'Please select a template', variant: 'destructive' });
                    return;
                  }

                  // Find the full template object from availableTemplates
                  const selectedTemplate = (availableTemplates[welcomeTemplateDialog.channelConfigId] || [])
                    .find(t => t.template_id === selectedTemplateId);
                  // ✅ CONSOLE 3 — Check selectedTemplate full object
                  console.log('[WM][3] selectedTemplateId:', selectedTemplateId);
                  console.log('[WM][3] selectedTemplate found:', JSON.stringify(selectedTemplate, null, 2));
                  console.log('[WM][3] channel:', welcomeTemplateDialog.channel);

                  const selectedChannelType = selectedTemplate?.channel_type;

                  // Extract wm_variables and correct wm_template_id based on channel
                  const wm_variables = selectedTemplate
                    ? extractWmVariables(selectedTemplate)               // ← no channelName needed
                    : null;

                  const wm_template_id_to_save = selectedTemplate
                    ? getWmTemplateId(selectedTemplate, selectedChannelType) // ← pass channel_type integer
                    : selectedTemplateId;

                  console.log('[WM][4] wm_variables computed:', wm_variables);
                  console.log('[WM][4] wm_template_id_to_save computed:', wm_template_id_to_save);

                  const wm_part_count = selectedTemplate?.part_count ?? 1;
                  console.log('[WM][4] wm_template_id_to_save part count:', wm_part_count);

                  const updatedOps = selectedOps.map(operator =>
                    operator.id === welcomeTemplateDialog.operatorId
                      ? {
                        ...operator,
                        welcomeMessage: true,
                        wm_template_id: wm_template_id_to_save,       // ← SMS: template_id, WA: ext_template_id
                        wm_template_name: selectedTemplateName,
                        wm_variables: wm_variables,
                        wm_part_count: wm_part_count,                   // ← NEW: variables map
                      }
                      : operator
                  );
                  console.log('[WM][5] updatedOps after save:', JSON.stringify(updatedOps.map(o => ({
                    id: o.id, welcomeMessage: o.welcomeMessage,
                    wm_template_id: o.wm_template_id, wm_variables: o.wm_variables,
                    wm_part_count: o.wm_part_count   // ← ADD THIS
                  })), null, 2));

                  setSelectedOperators(prev => ({
                    ...prev,
                    [welcomeTemplateDialog.channel]: {
                      ...prev[welcomeTemplateDialog.channel],
                      [welcomeTemplateDialog.country]: updatedOps
                    }
                  }));

                  toast({ title: 'Success', description: 'Welcome template linked successfully' });
                  setWelcomeTemplateDialog(null);
                  setSelectedTemplateId(null);
                  setSelectedTemplateName('');
                }}
                className="bg-[#3A85F7] hover:bg-[#2968c8] w-[100px]"
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AllocationCard;
